import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useController, useForm } from 'react-hook-form';
import { v4 } from 'uuid';
import classnames from 'classnames';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import { addToastNotification } from 'redux/actions';
import { createUserPreference } from 'redux/UserPreferences';
import Modal from 'components/shared/Modal/Modal';
import { ProgressIndicator } from 'components/shared/Progress';
import ToggleButton from 'components/shared/ToggleButton';
import { usePreferenceFormResolver } from '../../hooks';
import styles from './ChartSetPreferenceFormModal.module.css';

function ChartSetPreferenceFormModal({ query, charts, onClose, onSelect }) {
  const titleId = useMemo(() => v4(), []);
  const errorMessageId = useMemo(() => v4(), []);
  const dispatch = useDispatch();
  const defaultValues = useMemo(
    () => ({
      name: 'my new chart set',
      default: false,
      category: USER_PREFERENCE_SETS.CHART_SET,
      state: { query, charts }
    }),
    [query, charts]
  );
  const resolver = usePreferenceFormResolver({ category: USER_PREFERENCE_SETS.CHART_SET });
  const methods = useForm({
    mode: 'onBlur',
    defaultValues,
    resolver
  });
  const { handleSubmit, control, formState } = methods;
  const { isSubmitting, isValid, errors } = formState;
  const { field: nameField, fieldState: nameFieldSate } = useController({ name: 'name', control });
  const { field: defaultField } = useController({ name: 'default', control });
  const onSubmit = useCallback(
    async (values) => {
      try {
        const result = await dispatch(createUserPreference(values));
        dispatch(
          addToastNotification({
            id: v4(),
            title: values.name,
            description: 'successfully created'
          })
        );
        onSelect(result.preferenceId);
        onClose();
      } catch (error) {
        logger.error(error);
        dispatch(
          addToastNotification({
            id: v4(),
            title: values.name,
            description: 'failed to create.'
          })
        );
      }
    },
    [dispatch, onClose, onSelect]
  );

  return (
    <Modal aria-labelledby={titleId} animateX={false} global close={onClose} noPadding height={320} width={780}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        {isSubmitting && <ProgressIndicator />}
        <div id={titleId} className={styles.header}>
          Save chart set as
        </div>
        <div className={styles.body}>
          <div className={styles.field}>
            <div className={styles.control}>
              <input
                type="text"
                aria-label="Name"
                aria-invalid={nameFieldSate.invalid}
                aria-describedby={errorMessageId}
                name={nameField.name}
                value={nameField.value}
                className={styles.input}
                onChange={nameField.onChange}
                onBlur={nameField.onBlur}
              />
              <ToggleButton
                name={defaultField.name}
                className={styles.defaultToggle}
                checked={defaultField.value}
                onChange={defaultField.onChange}
              >
                Make default
              </ToggleButton>
            </div>
            <div id={errorMessageId} className={styles.message}>
              {nameFieldSate.invalid ? errors.name.message : ''}
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={classnames(styles.submitButton, {
              [styles.disabledButton]: !isValid || isSubmitting
            })}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ChartSetPreferenceFormModal;
